import type { AxiosResponse } from 'axios';

import { httpGET } from '../services/http';

/** Wire shape of GET /catalogue/products/:id (LampProductDetailsResponse). */
type CatalogueProductApiResponse = {
  id?: number;
  name?: string;
  description?: string;
  seriesId?: number;
  manufacturerId?: number;
  imageId?: number;
  imageUrl?: string;
  status?: string;
  variants?: Array<{
    id?: number;
    name?: string;
    power?: number;
    lumenOutput?: number;
    colorTemperature?: number;
    manufacturerUrl?: string;
    ldcFileId?: number;
    ldcFileUrl?: string;
  }>;
};

type CatalogueProductInfo = {
  name?: string;
  seriesId?: number;
  manufacturerId?: number;
  variants?: Array<{ id: number; name?: string; lumenOutput?: number }>;
} | null;

/** Process-scoped cache — shared across all components so no duplicate fetches. */
const cache = new Map<number, Promise<CatalogueProductInfo>>();

function toOptionalId(value: number | undefined): number | undefined {
  return value != null && Number.isFinite(value) ? value : undefined;
}

function normalizeCatalogueProduct(raw: CatalogueProductApiResponse | null | undefined): CatalogueProductInfo {
  if (raw == null) return null;

  const variants: Array<{ id: number; name?: string; lumenOutput?: number }> = [];
  if (Array.isArray(raw.variants)) {
    for (const variant of raw.variants) {
      const id = toOptionalId(variant.id);
      if (id == null) continue;
      const normalized: { id: number; name?: string; lumenOutput?: number } = { id };
      if (typeof variant.name === 'string') normalized.name = variant.name;
      if (typeof variant.lumenOutput === 'number' && Number.isFinite(variant.lumenOutput)) {
        normalized.lumenOutput = variant.lumenOutput;
      }
      variants.push(normalized);
    }
  }

  return {
    name: typeof raw.name === 'string' ? raw.name : undefined,
    seriesId: toOptionalId(raw.seriesId),
    manufacturerId: toOptionalId(raw.manufacturerId),
    variants: variants.length > 0 ? variants : undefined,
  };
}

export function fetchCatalogueProduct(productId: number): Promise<CatalogueProductInfo> {
  let entry = cache.get(productId);
  if (!entry) {
    entry = httpGET(`/catalogue/products/${productId}`)
      .then(({ data }: AxiosResponse<CatalogueProductApiResponse>) => normalizeCatalogueProduct(data))
      .catch((err) => {
        console.error(`Failed to fetch catalogue product ${productId}:`, err);
        cache.delete(productId);
        return null;
      });
    cache.set(productId, entry);
  }
  return entry;
}
